import React, { FC, useState } from "react";
import dashStyles from "../Dashboard/Dashboard.module.scss";
import styles from "./MusicTheory.module.scss";
import SvgIcon from "@mui/material/SvgIcon";
import Quiz from "../Quiz/Quiz";

function StaffCrotchetIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <line x1="2" y1="6" x2="22" y2="6" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="8" x2="22" y2="8" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="10" x2="22" y2="10" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="14" x2="22" y2="14" stroke="currentColor" strokeWidth="0.5" />
      <ellipse cx="12" cy="11" rx="2.2" ry="1.6" fill="currentColor" transform="rotate(-15, 12, 11)" />
      <line x1="14" y1="10.5" x2="14" y2="4" stroke="currentColor" strokeWidth="1.2" />
    </SvgIcon>
  );
}

function SharpSymbol({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <line x1="-1" y1="-3" x2="-1" y2="3" stroke="currentColor" strokeWidth="0.6" />
      <line x1="1" y1="-3" x2="1" y2="3" stroke="currentColor" strokeWidth="0.6" />
      <line x1="-2" y1="-1" x2="2" y2="0" stroke="currentColor" strokeWidth="0.5" />
      <line x1="-2" y1="0" x2="2" y2="1" stroke="currentColor" strokeWidth="0.5" />
    </g>
  );
}

function StaffKeySignatureIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <line x1="2" y1="6" x2="22" y2="6" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="8" x2="22" y2="8" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="10" x2="22" y2="10" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="0.5" />
      <line x1="2" y1="14" x2="22" y2="14" stroke="currentColor" strokeWidth="0.5" />
      <SharpSymbol x={5} y={6} />
      <SharpSymbol x={9} y={9} />
      <SharpSymbol x={13} y={12} />
      <SharpSymbol x={17} y={8} />
    </SvgIcon>
  );
}

function BeamedNotesIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <ellipse cx="7" cy="17" rx="2.4" ry="1.8" fill="currentColor" transform="rotate(-15, 7, 17)" />
      <ellipse cx="16" cy="17" rx="2.4" ry="1.8" fill="currentColor" transform="rotate(-15, 16, 17)" />
      <line x1="9.2" y1="16.5" x2="9.2" y2="5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="18.2" y1="16.5" x2="18.2" y2="5" stroke="currentColor" strokeWidth="1.2" />
      <line x1="9" y1="5.6" x2="18.4" y2="5.6" stroke="currentColor" strokeWidth="2.2" />
    </SvgIcon>
  );
}

interface SubTile {
  id: string;
  label: string;
}

interface MusicTheoryProps {
  /** Called with the subtile label when a subtile is opened, null when closed. */
  onSubSectionChange?: (label: string | null) => void;
}

// Add new subtiles here as the section grows.
const subTiles: SubTile[] = [
  { id: "note-identification",   label: "Note Identification" },
  { id: "key-signature",         label: "Key Signature" },
  { id: "rhythm-identification", label: "Rhythm Identification" },
];

const MusicTheory: FC<MusicTheoryProps> = ({ onSubSectionChange }) => {
  const [activeSubTile, setActiveSubTile] = useState<SubTile | null>(null);

  const openSubTile = (tile: SubTile) => {
    setActiveSubTile(tile);
    onSubSectionChange?.(tile.label);
  };

  if (activeSubTile) {
    return (
      <Quiz subtileId={activeSubTile.id} subtileLabel={activeSubTile.label} />
    );
  }

  return (
    <section className={styles.MusicTheory__grid}>
      {subTiles.map((tile) => (
        <div
          key={tile.id}
          className={dashStyles.Dashboard__tab}
          onClick={() => openSubTile(tile)}
        >
          {tile.id === "key-signature" ? (
            <StaffKeySignatureIcon className={dashStyles.Dashboard__tab_icon} />
          ) : tile.id === "rhythm-identification" ? (
            <BeamedNotesIcon className={dashStyles.Dashboard__tab_icon} />
          ) : (
            <StaffCrotchetIcon className={dashStyles.Dashboard__tab_icon} />
          )}
          {tile.label}
        </div>
      ))}
    </section>
  );
};

export default MusicTheory;
